import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchableMultiSelect } from '@speles7172/controls';
import { EntityModal } from '../common/EntityModal';
import { SearchableSelect } from '../common/SearchableSelect';
import { extractApiErrorMessage } from '../../utils/apiError';
import {
  peopleOptions,
  postOpeningMessage,
  startConversation,
  type RecordScope,
} from '../../api/notifications';
import { fetchRecordContext, fetchRecordFolders, type ThreadContext } from '../../api/threadTools';
import { ConversationToolbar } from './ConversationToolbar';
import {
  applyConversationTools,
  hasToolIntent,
  mergeToolIntent,
  summariseToolIntent,
  type ConversationToolIntent,
} from './conversationTools';

/**
 * Starting a conversation — a *new* one (COMMUNICATIONS-DESIGN H4).
 *
 * ⚠️ **A record has conversations, plural, and the app behaved as though it had
 * one.** The control on an event opened `for-record`'s first thread, so every
 * later subject continued there: the caterer added for a parking question could
 * read the whole exchange with the client about money. A conversation exists to
 * have its own people, and it cannot until there is a way to open a second one.
 *
 * ⚠️ **The same dialog for a record and for nothing.** On an event the record is
 * fixed and only the people and the subject are asked for; on the conversations
 * page there is no record yet, so it offers one — an event, or nothing at all,
 * because "ring the venue about next season" is a real conversation that
 * belongs to no event. That is one component with one field switched on, not
 * two dialogs that will drift.
 *
 * ⚠️ **It carries the same tools an open conversation has.** Attaching a file,
 * attaching a report and asking for a file back were written against a
 * `threadId` and so lived only beside a conversation that already existed —
 * which meant the one moment a producer most wants to send the summary, the
 * moment they are writing the opening message, was the one moment they could
 * not. Nothing about *choosing* needs the thread; only applying does. So the
 * choices are staged here and applied the instant `POST /threads` answers.
 *
 * ⚠️ **Recipients come from `/options/people`, described by what puts them on
 * the record.** The same picker the thread panel uses, scoped the same way —
 * "Vendor · Bella Catering" rather than an email address, because that is the
 * question somebody choosing recipients is actually asking. Scoping is a
 * convenience and never a control: `visibleTo` is still the boundary, and the
 * server checks every id against it.
 */
export interface NewConversationModalProps {
  open: boolean;
  onClose: () => void;
  /** Opened once the conversation exists, so it can be shown straight away. */
  onCreated: (threadId: string) => void;
  /**
   * The record it is about, when that is already settled.
   *
   * Omitted on the conversations page, which then offers the event picker and
   * lets the sender leave it attached to nothing.
   */
  scope?: RecordScope;
}

export function NewConversationModal({
  open,
  onClose,
  onCreated,
  scope,
}: NewConversationModalProps) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [eventId, setEventId] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** What the toolbar has been asked for, waiting on a thread to apply it to. */
  const [staged, setStaged] = useState<ConversationToolIntent>({});
  const [recordContext, setRecordContext] = useState<ThreadContext | null>(null);
  /**
   * The conversation, once it exists.
   *
   * ⚠️ **Set before the staged tools run, and it is what stops a second one.**
   * `POST /threads` succeeding and an attachment failing is a half-done submit;
   * pressing the button again must retry the attachment, never start another
   * conversation with the same subject and the same people.
   */
  const [startedId, setStartedId] = useState<string | null>(null);
  /**
   * Whether the opening message has already gone.
   *
   * ⚠️ **Its own flag, not inferred from `startedId`.** The thread and the
   * message are two writes, and the second can be the one that half-fails — a
   * refused file request leaves the dialog open with the message already sent.
   * Pressing the button again must not say the same thing to everybody twice.
   */
  const [openingPosted, setOpeningPosted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSubject('');
    setBody('');
    setRecipientIds([]);
    setEventId('');
    setError(null);
    setBusy(false);
    setStaged({});
    setStartedId(null);
    setOpeningPosted(false);
  }, [open]);

  /*
   * ⚠️ **Keyed on the two strings, never on the `scope` object.** Callers pass
   * it as a literal — `scope={{ entityType, entityId }}` — so it is a new
   * object on every render of the parent, and a memo depending on it is no memo
   * at all. That churned the people transport on every keystroke elsewhere in
   * the dialog (the very thing the note below warns about) and refired the
   * context lookup with it.
   */
  const scopeType = scope?.entityType;
  const scopeId = scope?.entityId;

  /**
   * The record the conversation will hang off.
   *
   * A fixed scope wins; otherwise the chosen event, or nothing. `Event` is the
   * registry's spelling and not a synonym — `THREAD_ENTITY_TYPES` is one
   * vocabulary, and a second spelling is a second conversation on one record.
   */
  const record = useMemo<RecordScope | null>(() => {
    if (scopeType && scopeId) return { entityType: scopeType, entityId: scopeId };
    return eventId ? { entityType: 'Event', entityId: eventId } : null;
  }, [scopeType, scopeId, eventId]);

  /*
   * Where this conversation's files would live, asked of the same server-side
   * walk the open-conversation toolbar uses. It decides which folder the picker
   * opens on and whether there is an event behind the record — which is what
   * settles whether reports are offered at all.
   */
  useEffect(() => {
    if (!open) return;
    let live = true;

    /*
     * ⚠️ **Both cleared before the new answer is asked for.** Leaving them
     * standing means the window between changing the event and its context
     * arriving shows the *previous* event's tools: the picker opens on its
     * folder and its reports are offered, so a file belonging to one event can
     * be staged onto a conversation about another. A moment with no tools is
     * the honest state — the record has changed and the answer is not known.
     *
     * The staged choices go with it for the same reason, and it is the stronger
     * one: a file id is absolute, so a file picked out of one event's folder
     * would attach to the other event's conversation however long the lookup
     * took. A fixed `scope` never changes, so nothing is ever lost this way on
     * a record's own dialog.
     */
    setRecordContext(null);
    setStaged({});

    fetchRecordContext(record)
      .then((resolved) => {
        if (live) setRecordContext(resolved);
      })
      // A record whose scope cannot be resolved still starts a conversation; it
      // just offers no folder-backed tools. Failing the dialog over the toolbar
      // would be the tail wagging the dog.
      .catch(() => {
        if (live) setRecordContext(null);
      });

    return () => {
      live = false;
    };
  }, [open, record]);

  /*
   * ⚠️ **Held still, like every other transport in this application.** The
   * picker keys its effects on the object's identity, so an inline literal is a
   * new transport on every keystroke and therefore a search that never settles.
   */
  const people = useMemo(() => peopleOptions(record ?? undefined), [record]);

  /**
   * Where a file asked for here would be filed.
   *
   * Record-keyed, because there is no thread yet. Handed to the toolbar as a
   * fetcher rather than fetched here, so the walk only runs if somebody opens
   * the file-request dialog — and memoised on `record` so that changing the
   * event drops the previous one's folders instead of offering them for this
   * conversation.
   */
  const loadFolders = useCallback(() => fetchRecordFolders(record), [record]);

  const staging = useMemo(() => summariseToolIntent(staged), [staged]);

  async function submit() {
    setError(null);
    setBusy(true);
    let threadId = startedId;
    try {
      if (threadId === null) {
        /*
         * ⚠️ **Started empty, and the body follows as a message.** `POST
         * /threads` posts the opening message through the package's own
         * `startThread`, which passes neither `attachments` nor `perRecipient`
         * — so a body sent here goes out as an email that *cannot* carry the
         * files or the upload button, and it goes out before the staged tools
         * have even run. That is why a report staged in this dialog arrived in
         * the app and not in the mail, and why asking for a file back turned
         * into a second message with a second email.
         *
         * A thread with no message is a state the package declares
         * ("a thread may legitimately start empty") and creating one notifies
         * nobody, so nothing is emailed twice by splitting.
         */
        const thread = await startConversation({
          subject: subject.trim() || undefined,
          recipientIds,
          record,
        });
        threadId = thread.id;
        setStartedId(threadId);
      }
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          err instanceof Error ? err.message : 'The conversation could not be started.',
        ),
      );
      setBusy(false);
      return;
    }

    /*
     * ⚠️ **Reported separately, because by here the conversation exists.** A
     * report that fails to render must not read as "it did not start" — that
     * sends somebody back to write the whole thing again, and they end up with
     * two. The button becomes "Open the conversation" instead, and pressing it
     * again retries only the attachments.
     */
    try {
      /*
       * ⚠️ **The files land first, and the ask is *not* applied here.**
       * `applyConversationTools` would post the request as a message of its own
       * — the second email this whole design exists to prevent. It rides the
       * opening message below instead, which is also what puts the freshly
       * attached files on that message's mail: `pendingMailAttachments` reads
       * what is on the thread at the moment it posts, so they have to be there
       * first.
       *
       * ⚠️ **What succeeded is dropped as it succeeds.** A retry that replayed
       * the whole intent would attach the files a second time and email
       * everybody a second set of single-use links — the failure would cost
       * more than it did. The narrowing keeps the ask, which has not run.
       */
      const attachments: ConversationToolIntent = {
        ...(staged.fileIds ? { fileIds: staged.fileIds } : {}),
        ...(staged.reports ? { reports: staged.reports } : {}),
      };
      if (hasToolIntent(attachments)) {
        await applyConversationTools(threadId, attachments, (remaining) =>
          setStaged({ ...remaining, ...(staged.upload ? { upload: staged.upload } : {}) }),
        );
      }

      /*
       * ⚠️ **One opening message, carrying everything.** The body, the files
       * just attached and the upload button reach every recipient as a single
       * email — which is the whole point, and what `POST /threads` cannot do.
       *
       * ⚠️ **Both texts are kept when both were typed.** The dialog asks for a
       * message *and*, inside "Ask for a file back", what is being asked for.
       * Dropping either silently is the fault this branch already fixed once.
       */
      const opening = [body.trim(), staged.upload?.note?.trim()].filter(Boolean).join('\n\n');
      const asked = staged.upload;
      if (!openingPosted && (opening !== '' || asked !== undefined)) {
        const posted = await postOpeningMessage(
          threadId,
          // A button with no words is worse than none: an ask with nothing
          // written falls back to the same sentence the standalone endpoint uses.
          opening || 'Please send us a file when you get a moment.',
          asked ?? null,
        );
        /*
         * ⚠️ **Recorded before anything is judged, because the message is
         * posted.** Whatever is wrong with the ask, pressing the button again
         * must not say the same thing to everybody twice.
         */
        setOpeningPosted(true);
        setStaged((current) => ({ ...current, upload: undefined }));

        /*
         * ⚠️ **A message that posted is not an ask that went out.** The endpoint
         * mints the links and posts the message regardless — nobody to email, or
         * a folder that is not the record's, must not cost the sender the words
         * they wrote — so a refused ask comes back as a perfectly successful
         * `201` carrying `requested: 0`. Closing on that told somebody their
         * file request had been sent when no recipient got a button.
         *
         * The conversation and the message are real, so this is reported rather
         * than thrown: the dialog stays open with the error, and its button
         * already reads "Open the conversation".
         */
        if (asked !== undefined && (posted.requested ?? 0) === 0) {
          setError(
            'The conversation started and your message was sent, but the file request could not go out — ' +
              'nobody on it has an email address, or the folder is not under this record. ' +
              'Ask again from inside the conversation.',
          );
          setBusy(false);
          return;
        }
      }
    } catch (err) {
      setError(
        extractApiErrorMessage(
          err,
          err instanceof Error ? err.message : 'That could not be attached.',
        ),
      );
      setBusy(false);
      return;
    }

    setBusy(false);
    onCreated(threadId);
    onClose();
  }

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="New conversation"
      size="md"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="start-conversation"
          >
            {busy
              ? startedId === null
                ? 'Starting…'
                : 'Attaching…'
              : startedId === null
                ? 'Start conversation'
                : 'Open the conversation'}
          </button>
        </div>
      }
    >
      <div className="peles-controls space-y-4">
        {error && (
          <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </p>
        )}

        {/*
          Only where the record is not already settled. On an event this would
          be a field with one possible answer, already given.
        */}
        {!scope && (
          <div>
            <label
              htmlFor="new-conversation-event"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              About an event
            </label>
            <SearchableSelect
              entityType="event"
              value={eventId}
              onChange={(value) => setEventId(Array.isArray(value) ? (value[0] ?? '') : value)}
              placeholder="Search events…"
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave it empty for a general conversation that is not about an event.
            </p>
          </div>
        )}

        <div>
          <label
            htmlFor="new-conversation-subject"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Subject
          </label>
          <input
            id="new-conversation-subject"
            type="text"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
            placeholder="What is it about?"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1">People</span>
          <SearchableMultiSelect
            transport={people}
            value={recipientIds}
            onChange={setRecipientIds}
            placeholder="Add people"
            data-testid="new-conversation-people"
          />
          <p className="mt-1 text-xs text-gray-500">
            You are on it either way. Everybody here can read the whole conversation.
          </p>
        </div>

        <div>
          <label
            htmlFor="new-conversation-body"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            First message
          </label>
          <textarea
            id="new-conversation-body"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={4}
            placeholder="Optional — you can also start it empty and write later."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/*
          The same three tools an open conversation has. They are staged rather
          than done: there is no thread to attach to until the button below is
          pressed, and the summary is there because a choice made in a dialog
          that has since closed is otherwise invisible until it has happened.
        */}
        <div className="pt-2 border-t border-gray-100">
          <span className="block text-sm font-medium text-gray-700 mb-2">Send with it</span>
          <ConversationToolbar
            context={recordContext}
            disabled={busy}
            pending
            loadFolders={loadFolders}
            onIntent={(intent) => setStaged((current) => mergeToolIntent(current, intent))}
          />
          {staging.length > 0 && (
            <div
              className="mt-2 flex flex-wrap items-center gap-2"
              data-testid="new-conversation-staged"
            >
              {staging.map((part) => (
                <span
                  key={part}
                  className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs"
                >
                  {part}
                </span>
              ))}
              <button
                type="button"
                onClick={() => setStaged({})}
                className="text-xs text-gray-500 underline hover:text-gray-700"
                data-testid="new-conversation-clear-staged"
              >
                Clear
              </button>
            </div>
          )}
          {!recordContext?.eventId && (
            <p className="mt-2 text-xs text-gray-500">
              Reports are offered once the conversation is about an event.
            </p>
          )}
        </div>
      </div>
    </EntityModal>
  );
}
